import React, { useState } from 'react';
import styled from 'styled-components';
import { useForm, ValidationError } from '@formspree/react';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  FaEnvelope, FaLinkedin, FaGithub, FaMapMarkerAlt, FaPhone, FaPaperPlane,
} from 'react-icons/fa';
import { FiDownload } from 'react-icons/fi';
import Reveal from './ui/Reveal';
import { useI18n } from '../context/LanguageContext';
import { useThemeMode } from '../theme/ThemeContext';

const Section = styled.section`
  max-width: var(--max-width);
  margin: 0 auto;
  padding: 6rem 2rem;
  @media (max-width: 768px) { padding: 4.5rem 1.25rem; }
`;

const Head = styled.div`
  text-align: center; margin-bottom: 3rem;
  h2 { font-size: clamp(2rem, 4.5vw, 2.9rem); margin-bottom: 1rem; }
  p { color: var(--text-muted); max-width: 560px; margin: 0 auto; }
`;

const Grid = styled.div`
  display: grid; grid-template-columns: 0.9fr 1.1fr; gap: 1.75rem; align-items: stretch;
  @media (max-width: 820px) { grid-template-columns: 1fr; }
`;

const Info = styled.div`
  border: 1px solid var(--border); background: var(--surface);
  border-radius: 22px; padding: 2rem; display: flex; flex-direction: column; gap: 0.85rem;
  h3 { font-size: 1.3rem; margin-bottom: 0.2rem; }
  > p { color: var(--text-muted); margin-bottom: 0.8rem; line-height: 1.7; }
`;

const Row = styled.a`
  display: flex; align-items: center; gap: 0.9rem;
  padding: 0.8rem 1rem; border-radius: 14px;
  border: 1px solid var(--border); background: var(--bg-alt);
  transition: border-color 0.25s ease, transform 0.25s ease, background 0.25s ease;
  .icon {
    display: grid; place-items: center; width: 40px; height: 40px; border-radius: 11px;
    background: var(--surface); color: var(--primary-light); font-size: 1.05rem; flex-shrink: 0;
  }
  .meta { display: flex; flex-direction: column; }
  .lbl { font-size: 0.74rem; color: var(--text-faint); }
  .val { font-size: 0.94rem; color: var(--text); font-weight: 500; word-break: break-word; }
  &:hover { border-color: var(--primary-light); background: var(--surface-hover); transform: translateX(4px); }
`;

const CvLink = styled.a`
  display: inline-flex; align-items: center; justify-content: center; gap: 0.55rem;
  margin-top: 0.4rem; padding: 0.85rem 1.3rem; border-radius: 12px;
  background: var(--gradient-accent); color: #fff; font-weight: 600;
  box-shadow: 0 12px 30px -14px rgba(99,102,241,0.7);
  transition: transform 0.2s ease;
  &:hover { transform: translateY(-2px); }
`;

const Form = styled.form`
  border: 1px solid var(--border); background: var(--surface);
  border-radius: 22px; padding: 2rem; display: flex; flex-direction: column; gap: 1.2rem;
  overflow: hidden;
`;

const TermBar = styled.div`
  display: flex; align-items: center; gap: 0.45rem;
  margin: -2rem -2rem 0;
  padding: 0.7rem 1.2rem;
  border-bottom: 1px solid var(--border);
  background: var(--bg-alt);
  span.dot { width: 11px; height: 11px; border-radius: 50%; }
  .red { background: #ff5f57; }
  .yellow { background: #febc2e; }
  .green { background: #28c840; }
  .title {
    margin-left: 0.5rem;
    font-family: Consolas, 'Courier New', monospace;
    font-size: 0.76rem;
    color: var(--text-faint);
    letter-spacing: 0.02em;
  }
`;

const Field = styled.div`
  display: flex; flex-direction: column; gap: 0.5rem;
  label {
    font-family: Consolas, 'Courier New', monospace;
    font-size: 0.85rem; font-weight: 500; color: var(--text-muted);
  }
  label::before { content: '> '; color: var(--primary-light); font-weight: 700; }
  input, textarea {
    width: 100%; padding: 0.9rem 1rem; font-size: 0.98rem; font-family: var(--font-body);
    color: var(--text); background: var(--bg-alt); border: 1px solid var(--border);
    border-radius: 12px; outline: none; resize: vertical;
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
  }
  input::placeholder, textarea::placeholder { color: var(--text-faint); }
  input:focus, textarea:focus {
    border-color: var(--primary-light); box-shadow: 0 0 0 3px rgba(99,102,241,0.18);
  }
`;

const Submit = styled.button`
  display: inline-flex; align-items: center; justify-content: center; gap: 0.6rem;
  padding: 0.95rem 1.5rem; font-size: 1rem; font-weight: 600; color: #fff;
  border: none; border-radius: 12px; cursor: pointer;
  background: var(--gradient-accent); box-shadow: 0 12px 30px -12px rgba(99,102,241,0.7);
  transition: transform 0.25s ease, box-shadow 0.25s ease, opacity 0.25s ease;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 16px 36px -12px rgba(236,72,153,0.7); }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Note = styled.p`
  font-size: 0.92rem; text-align: center;
  color: ${({ $success }) => ($success ? '#34d399' : '#fca5a5')};
`;

const Contact = () => {
  const { t, data } = useI18n();
  const { profile } = data;
  const [state, handleSubmit] = useForm('mldjekaq');
  const [captchaOk, setCaptchaOk] = useState(false);
  const { theme } = useThemeMode();

  return (
    <Section id="contact">
      <Reveal>
        <Head>
          <span className="eyebrow">{t.contact.eyebrow}</span>
          <h2>{t.contact.h2a}<span className="gradient-text">{t.contact.h2b}</span></h2>
          <p>{t.contact.sub}</p>
        </Head>
      </Reveal>

      <Grid>
        <Reveal>
          <Info>
            <h3>{t.contact.detailsH}</h3>
            <p>{t.contact.detailsSub}</p>

            <Row href={`mailto:${profile.email}`}>
              <span className="icon"><FaEnvelope /></span>
              <span className="meta"><span className="lbl">{t.contact.email}</span><span className="val">{profile.email}</span></span>
            </Row>
            <Row href={`tel:${profile.phoneHref}`}>
              <span className="icon"><FaPhone /></span>
              <span className="meta"><span className="lbl">{t.contact.phone}</span><span className="val">{profile.phone}</span></span>
            </Row>
            <Row href={profile.linkedin} target="_blank" rel="noopener noreferrer">
              <span className="icon"><FaLinkedin /></span>
              <span className="meta"><span className="lbl">LinkedIn</span><span className="val">Simon El Khoury El Saifi</span></span>
            </Row>
            <Row href={profile.github} target="_blank" rel="noopener noreferrer">
              <span className="icon"><FaGithub /></span>
              <span className="meta"><span className="lbl">GitHub</span><span className="val">{profile.githubHandle}</span></span>
            </Row>
            <Row as="div" style={{ cursor: 'default' }}>
              <span className="icon"><FaMapMarkerAlt /></span>
              <span className="meta"><span className="lbl">{t.contact.basedIn}</span><span className="val">{profile.location} · {t.contact.basedVal}</span></span>
            </Row>

            <CvLink href={`${process.env.PUBLIC_URL}/${profile.cv}`} download>
              <FiDownload /> {t.contact.downloadCv}
            </CvLink>
          </Info>
        </Reveal>

        <Reveal delay={0.08}>
          <Form onSubmit={handleSubmit}>
            <TermBar>
              <span className="dot red" />
              <span className="dot yellow" />
              <span className="dot green" />
              <span className="title">{t.contact.formTitle}</span>
            </TermBar>
            <Field>
              <label htmlFor="email">{t.contact.formEmail}</label>
              <input id="email" type="email" name="email" placeholder={t.contact.emailPh} required />
              <ValidationError prefix="Email" field="email" errors={state.errors} />
            </Field>
            <Field>
              <label htmlFor="message">{t.contact.formMsg}</label>
              <textarea id="message" name="message" rows="5" placeholder={t.contact.msgPh} required />
              <ValidationError prefix="Message" field="message" errors={state.errors} />
            </Field>

            <ReCAPTCHA
              key={theme}
              sitekey="6LehfPMqAAAAAEZW5oJXgDJer1DfENrK21j82o_D"
              theme={theme === 'light' ? 'light' : 'dark'}
              onChange={(v) => setCaptchaOk(!!v)}
              onExpired={() => setCaptchaOk(false)}
            />

            <Submit type="submit" disabled={state.submitting || !captchaOk}>
              <FaPaperPlane />
              {state.submitting ? t.contact.sending : t.contact.send}
            </Submit>

            {state.succeeded && <Note $success>{t.contact.success}</Note>}
          </Form>
        </Reveal>
      </Grid>
    </Section>
  );
};

export default Contact;
